import { useRef, useEffect } from "react";

// canvas-confetti solo se usa en el huevo de pascua del logo: se baja cuando
// alguien empieza a tocarlo (el primer click), no con el bundle de entrada.
const importConfetti = () => import("canvas-confetti").then((module) => module.default);

type Confetti = Awaited<ReturnType<typeof importConfetti>>;

let confettiReady: Promise<Confetti> | undefined;

function loadConfetti() {
  // Un chunk que no llego no se queda guardado como rechazo: el proximo click reintenta.
  return (confettiReady ??= importConfetti().catch((error) => {
    confettiReady = undefined;
    throw error;
  }));
}

export function useEasterEgg(targetClicks: number = 5, resetTimeout: number = 2000) {
  // Ref y no state: el contador no se pinta en ningun lado, y como state
  // provocaba un re-render del layout entero en cada click del logo.
  const clickCountRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const triggerFireworks = (confetti: Confetti, x: number, y: number) => {
    const xRatio = x / window.innerWidth;
    const yRatio = y / window.innerHeight;

    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 2147483647 };

    const randomInRange = (min: number, max: number) =>
      Math.random() * (max - min) + min;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(intervalRef.current!);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: {
          x: xRatio + randomInRange(-0.1, 0.1),
          y: yRatio + randomInRange(-0.1, 0.1),
        },
      });
    }, 250);
  };

  const handleBrandClick = (e: React.MouseEvent) => {
    clickCountRef.current += 1;

    // Precarga: cuando llegue el click que dispara los fuegos artificiales el
    // modulo ya esta, y salen sin demora.
    if (clickCountRef.current === 1) loadConfetti().catch(() => {});

    if (clickCountRef.current === targetClicks) {
      const { clientX, clientY } = e;
      loadConfetti()
        .then((confetti) => {
          // Si el layout se desmonto mientras bajaba el modulo, no se arranca un
          // interval que nadie va a limpiar.
          if (mountedRef.current) triggerFireworks(confetti, clientX, clientY);
        })
        .catch(() => {});
      clickCountRef.current = 0;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, resetTimeout);
  };

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // El interval sobrevivia al unmount y seguia disparando confetti 3s.
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return handleBrandClick;
}
